/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/naming-convention */

// deps
import React from "react";

// internal-deps
import { format, isEmpty } from "@swiggy-private/js-utils";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { SESSION_STORAGE_KEYS, REDIRECTIONAL_INFO } from "@swiggy-private/web-bridge-sdk";
import { currencyFormat, getAllBillEntries } from "@swiggy-private/dineout-payment-success-ui";
import { ICharge } from "@swiggy-private/checkout-types";

// components
import { DineoutCheckoutMainProps } from "@dineout-cart-client/components/dineout-checkout-main";

// interfaces
import {
    IBillEntries,
    IBookingDetails,
    IDineoutCartResponse,
    ILine,
    IOfferDetails,
    IRestaurantDetails,
    IUpsellWidgetItemProps,
} from "@dineout-cart-client/interfaces";
import { IDineoutCartResponseData } from "@swiggy-private/checkout-core";

import {
    IDiscount,
    IResponseCartItem,
    ISuperSavingBottomWidgetProps,
    ITicketData,
    IWidget,
    IStoresInfo,
} from "@swiggy-private/checkout-core";

import { ILocalCart, ILocalCartItem } from "@dineout-cart-client/interfaces/LocalCart";

// constants
import {
    BILL_ELEMENT_ENTRIES,
    BOOKING_STATUS_TO_COLORCODE_MAPPING,
    CART_PAGE_TEXTS,
    CART_UI_WIDGETIDS,
    LABEL_TYPES,
    OOS_POPUP_TEXT,
    SAVINGS_HEADER_WIDGET_TYPES,
} from "@dineout-cart-client/constants";
import { DINEOUT_CHECKOUT_INITIAL_STATE } from "@swiggy-private/checkout-core";
import { getTotalGstAmount } from "@dineout-cart-client/helpers/cartHelper";

const EMPTY_FUNCTION = () => void 0;

type IGetBookingTicketMeta =
    | {
          bookingDetails: IBookingDetails;
          offerDetails: IOfferDetails;
          restaurantDetails: IRestaurantDetails;
      }
    | undefined;

interface IGetUpsellWidgetProps {
    changeSuperBtnRef: React.RefObject<HTMLButtonElement>;
    addSuper: VoidFunction;
    removeSuper: VoidFunction;
    changeSuper: VoidFunction;
    itemData: IUpsellWidgetItemProps;
}

export const getBookingStatusColorcode = (status?: string): string | undefined => {
    if (!status) return;
    return BOOKING_STATUS_TO_COLORCODE_MAPPING[status.toLowerCase()] as string;
};

export const getBookingTicketMeta = (
    cartItem?: IResponseCartItem,
    storeInfo?: IStoresInfo,
    orderId?: string,
): IGetBookingTicketMeta => {
    if (!cartItem || !storeInfo) return;
    const {
        bookingDay,
        bookingDate,
        bookingSlot: bookingSlotGroupTitle,
        bookingSlotTime: bookingtime,
        guestCount,
        ticketAttributes,
        ticketPresentationInfo,
        bookingStatus,
    } = cartItem;

    const { name: restaurantName, locality: restaurantLocality, cityName: restaurantCity } = storeInfo;

    return {
        bookingDetails: {
            day: bookingDay,
            date: bookingDate,
            slotGroupTitle: bookingSlotGroupTitle,
            time: bookingtime,
            noOfGuests: Number(guestCount),
            status: bookingStatus || "",
            statusColor: getBookingStatusColorcode(bookingStatus) as keyof IColors,
            bookingId: orderId,
        },
        offerDetails: {
            title: ticketAttributes?.ticketTitle,
            chipSrc: ticketPresentationInfo?.ticketLogo,
            containerColorCode: ticketPresentationInfo?.colorCodes[0],
        },
        restaurantDetails: {
            name: restaurantName,
            locality: restaurantLocality,
            city: restaurantCity,
        },
    };
};

export const getUpsellWidgetProps = (
    superSavingBottomWidget?: ISuperSavingBottomWidgetProps,
): IGetUpsellWidgetProps => {
    const displayText = superSavingBottomWidget?.subscriptionData?.name || "";
    const chipText =
        superSavingBottomWidget?.lines?.find((line: ILine) =>
            line.labels.some((label) => label.type === LABEL_TYPES.TAG),
        )?.labels[0]?.value || "";
    const basePrice = superSavingBottomWidget?.subscriptionData?.basePrice;
    const finalPrice = superSavingBottomWidget?.subscriptionData?.finalPrice;
    const subtitleOne =
        superSavingBottomWidget?.lines?.find((line: ILine) =>
            line.labels.some((label) => label.type === LABEL_TYPES.TITLE),
        )?.labels[0]?.value || "";
    const logo = superSavingBottomWidget?.savingBenefitsImage;

    const itemData = {
        currency: "rupees",
        discount_message: "",
        display_text: displayText,
        hierarchy: 1,
        icon: "",
        info_text: "",
        intermediateText: "",
        key: "super_v3_nudge",
        type: "SUPER_V3_NUDGE",
        value: "",
        meta: {
            chipText,
            price: {
                basePrice,
                finalPrice,
            },
            nudge_type: "NUDGE_TYPE_REGULAR",
            cta: {
                ...superSavingBottomWidget?.subscriptionData?.cta,
                type: "DEEP_LINK",
            },
            subtitles: [subtitleOne],
            logo_ctx: {
                logo,
            },
        },
    };

    return {
        changeSuperBtnRef: React.createRef<HTMLButtonElement>(),
        addSuper: EMPTY_FUNCTION,
        removeSuper: EMPTY_FUNCTION,
        changeSuper: EMPTY_FUNCTION,
        itemData,
    };
};

export const getDiscounts = (discounts?: IDiscount[]): IDiscount[] => {
    const validDiscounts = discounts?.filter?.(
        (discount) => discount?.ctx?.isAggregatedDiscount || discount?.couponType,
    );
    validDiscounts?.forEach((discount: IDiscount, index: number, discountArr: IDiscount[]) => {
        discountArr[index].value = currencyFormat(Math.floor(Number(discount.value)));
    });
    return validDiscounts || [];
};

const getFeeObject = (charges: ICharge[]) => {
    let feeObject = null;
    charges?.find((item) => {
        if (item.type === BILL_ELEMENT_ENTRIES.CONVENIENCE_FEE_TYPE) {
            feeObject = item;
        }
    });
    return feeObject;
};

export const getBillDetails = (cartState?: IDineoutCartResponseData | null, isEventCart?: boolean): IBillEntries[] => {
    if (!cartState) return [];
    const formattedDiscounts = getDiscounts(cartState?.bill?.discounts);
    const subscriptionDetails = cartState?.superSavingBottomWidget?.subscriptionData;
    const feeObject = getFeeObject(cartState?.bill?.charges || []);
    const skipPayment = cartState?.bill?.skipPayment;
    const guestCount = Number(cartState?.items?.[0]?.guestCount);
    const advanceAmount = cartState?.bill?.dineoutPricingInfo?.advanceAmount || 0;
    const totalBillWithoutTip = cartState?.bill?.dineoutPricingInfo?.billAmountToApplyCoupon || 0;
    const totalBillAmount = cartState?.bill?.dineoutPricingInfo?.billToPay || 0;
    const redeemable = cartState?.items?.[0]?.ticketAttributes?.redeemable;
    const dineCash = cartState?.bill?.dineoutPricingInfo?.totalRewardsRedeemed;
    const gst = getTotalGstAmount(cartState);
    const enteredBillAmount = 0;
    const billItems = isEventCart // this condition is to avoid duplicate bill items for table reservation
        ? cartState?.items?.map((item) => {
              const { savingsLine, itemId, ticketAttributes } = item;
              const { totalEffectivePrice, totalBasePrice } = ticketAttributes || {};
              const strikethroughAmount =
                  totalBasePrice - totalEffectivePrice > 0
                      ? BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL + totalBasePrice?.toString()
                      : "";
              return {
                  id: itemId,
                  leftText: savingsLine || "",
                  rightText: totalEffectivePrice?.toString(),
                  strikethroughAmount: strikethroughAmount,
              };
          })
        : [];

    const billItemsCheck = billItems?.filter((item) => !isEmpty(item));

    return getAllBillEntries({
        discountList: formattedDiscounts,
        subscription: subscriptionDetails,
        feeObject,
        totalBillAmount: totalBillWithoutTip,
        toPayAmount: Math.floor(totalBillAmount),
        tip: "",
        advanceAmount: Math.floor(advanceAmount),
        enteredBillAmount: Math.floor(enteredBillAmount),
        isTableReservation: !skipPayment,
        guestCount,
        isCart: true,
        redeemable,
        billItems: billItemsCheck,
        dealCategory: cartState?.bill?.dealCategory,
        dineCash: dineCash || 0,
        gst: gst,
    });
};

export const updateConfig = (config: DineoutCheckoutMainProps["config"]) => {
    try {
        const isSessionStorageAvailable = typeof window !== "undefined" && typeof sessionStorage !== "undefined";
        const redirectinalInfo =
            isSessionStorageAvailable &&
            JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEYS.REDIRECTIONAL_INFO) ?? "{}");
        const paymentReturnUrl = redirectinalInfo[REDIRECTIONAL_INFO.PAYMENT_RETURN_URL] || "";
        const addMoneyReturnUrl = redirectinalInfo[REDIRECTIONAL_INFO.ADD_MONEY_RETURN_URL] || "";

        return {
            ...config,
            paymentReturnUrl,
            addMoneyReturnUrl,
        };
    } catch (err) {
        return config;
    }
};

export const getCTAText = (billAmount?: number, skipPayment?: boolean, isEventsBookingFlow?: boolean) => {
    if (skipPayment) {
        return isEventsBookingFlow
            ? CART_PAGE_TEXTS.FreeEventReservationProceedCta
            : CART_PAGE_TEXTS.FreeReservationProceedCta;
    }
    return format(CART_PAGE_TEXTS.PrebookReservationProceedCta, billAmount);
};

export const getBillEntriesList = (billEntries: IBillEntries[]) =>
    billEntries?.reduce(
        (details, billItem) => {
            if (billItem.leftElement?.text && billItem.rightElement?.text) {
                details[billItem.leftElement.text] = billItem.rightElement.text;
            }
            return details;
        },
        {} as Record<string, string>,
    );

export const getSavingWidgetData = (widgets: IWidget[] | undefined): IWidget | undefined => {
    if (isEmpty(widgets)) return;
    return widgets.find((widget) => SAVINGS_HEADER_WIDGET_TYPES.includes(widget.widgetInfo?.widgetType || ""));
};

export const getSpecialRequestWidget = (widgets?: IWidget[]): IWidget | undefined => {
    if (isEmpty(widgets)) return;
    return widgets.find((widget) => widget.widgetInfo?.widgetType === CART_UI_WIDGETIDS.SPECIAL_REQUEST_WIDGET);
};

export const getPopupData = (isCompleteOOS: boolean) => {
    return isCompleteOOS ? OOS_POPUP_TEXT.COMPLETE_OOS : OOS_POPUP_TEXT.PARTIAL_OOS;
};

export const getTicketDetailsIdList = (ticketItems?: { itemVariantId?: string; quantity?: number }[]) =>
    ticketItems?.reduce(
        (details, ticketItem) => {
            details[String(ticketItem?.itemVariantId || "")] = ticketItem?.quantity || 0;
            return details;
        },
        {} as Record<string, number>,
    );

export const getDealCategory = (cartState?: IDineoutCartResponse["data"] | null): string | undefined => {
    return cartState?.bill?.dealCategory;
};

export const getRestaurantId = (cartState?: IDineoutCartResponse["data"] | null): string | undefined => {
    return String(cartState?.storesInfo?.[0]?.id || "");
};

export const getSuperSavingBottomWidget = (
    cartState?: IDineoutCartResponse["data"] | null,
): ISuperSavingBottomWidgetProps | undefined => {
    return cartState?.superSavingBottomWidget;
};

export const getReservationTicketMeta = (cartItem?: IResponseCartItem): ITicketData | undefined => {
    return cartItem?.ticketAttributes;
};

export const prepareLocalCartFromReviewedCart = (
    flowType: string,
    currentLocalCart: ILocalCart,
    cartState: IDineoutCartResponse["data"],
): ILocalCart => {
    const updatedItemsPayload = currentLocalCart?.items?.map((localCartItem: ILocalCartItem) => {
        const correspondingItem = cartState?.items?.find(
            (reviewedCartItem) => String(localCartItem?.meta?.variantId) === String(reviewedCartItem.itemVariantId),
        );
        if (isEmpty(correspondingItem)) return localCartItem;
        return {
            ...localCartItem,
            quantity: correspondingItem?.quantity || 0,
            outOfStock: correspondingItem?.outOfStock || false,
        };
    });
    return {
        ...currentLocalCart,
        flowType,
        items: updatedItemsPayload,
    };
};

export const isReservationFlow = (initialState?: string) => {
    return (
        initialState === DINEOUT_CHECKOUT_INITIAL_STATE.TABLE_RESERVATION_CART ||
        initialState === DINEOUT_CHECKOUT_INITIAL_STATE.EVENT_BOOKING_CART
    );
};
